import React, { useCallback, useEffect, useRef, useState } from 'react';
import RevivalScene from './RevivalScene';
import './ServerShowcase.css';

/**
 * The Minecraft servers where Petlings live, one slide each.
 *
 * RevivalSMP runs RevivalPets, the server-side Petlings, and is the official
 * server. VanillaTown is its sister server: true vanilla survival, where the
 * Petlings mod brings your pet along. Each slide carries its own colours, so
 * the section turns from RevivalSMP's gothic night to VanillaTown's
 * green-and-red as it slides.
 */
export const SERVERS = [
  {
    id: 'revivalsmp',
    name: 'RevivalSMP',
    badge: 'Official Petlings Server',
    tagline: 'Where your pets come alive in multiplayer.',
    blurb:
      'Our home server runs RevivalPets, the server-side version of Petlings. Join with the mod and several pets ' +
      'can follow you, everyone sees your cosmetics, and you can meet other players’ companions.',
    features: ['Java + Bedrock', 'Multiple pets per player', 'Pet Worlds on RevivalWorlds', 'Multiplayer pet interactions'],
    primary: { label: 'Join RevivalSMP', href: 'https://revivalsmp.net' },
    secondary: { label: 'Pet Worlds', href: 'https://revivalworlds.com' },
    visual: <RevivalScene />,
    theme: { bg: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)', accent: '#FF6B00', accent2: '#E8C47A' },
  },
  {
    id: 'vanillatown',
    name: 'VanillaTown',
    badge: 'Sister Server · Season 1',
    tagline: 'One world. Two sides. War & peace.',
    blurb:
      'A whitelisted, true-vanilla survival world. Builders keep the peaceful west, raiders fight it out in the ' +
      'east, and the neutral middle belongs to no one. Petlings are supported: your pet comes along with the mod.',
    features: ['True vanilla survival', 'Peace · War · Neutral zones', 'Proximity voice chat', 'Java + Bedrock'],
    address: 'vanillatown.net',
    primary: { label: 'Apply on Discord', href: 'https://discord.gg/w3FZYMBZEZ' },
    secondary: { label: 'vanillatown.net', href: 'https://vanillatown.net' },
    visual: (
      <img
        src="/servers/vanillatown-hero.webp"
        alt="VanillaTown: builders on the peaceful side, raiders on the war side"
        className="server-scene"
        loading="lazy"
      />
    ),
    theme: { bg: 'linear-gradient(180deg, #0e0f14 0%, #131a24 100%)', accent: '#3ad46a', accent2: '#3fd8ff' },
  },
];

const ADVANCE_MS = 8000;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function ServerShowcase() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [copied, setCopied] = useState(null);
  const touchStart = useRef(null);
  const count = SERVERS.length;

  const go = useCallback((next) => setIndex((next + count) % count), [count]);

  // Slides on its own, but never while someone is reading or pointing at
  // it, and never for anyone who has asked for less motion.
  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), ADVANCE_MS);
    return () => clearInterval(timer);
  }, [paused, count]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') go(index + 1);
    if (e.key === 'ArrowLeft') go(index - 1);
  };

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
    touchStart.current = null;
  };

  const copy = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(address);
      setTimeout(() => setCopied(null), 1800);
    } catch (_) {
      // Clipboard can be blocked; the address is on screen to type anyway.
    }
  };

  const theme = SERVERS[index].theme;

  return (
    <section
      className="server-showcase"
      style={{ '--server-bg': theme.bg, '--server-accent': theme.accent, '--server-accent2': theme.accent2 }}
      aria-roledescription="carousel"
      aria-label="Servers where Petlings live"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="container">
        <div className="server-heading">
          <span className="server-eyebrow">Play with your pet</span>
          <div className="server-tabs" role="tablist">
            {SERVERS.map((server, i) => (
              <button
                key={server.id}
                role="tab"
                aria-selected={i === index}
                aria-controls={`server-slide-${server.id}`}
                className={`server-tab ${i === index ? 'active' : ''}`}
                onClick={() => go(i)}
              >
                {server.name}
              </button>
            ))}
          </div>
        </div>

        <div className="server-viewport">
          <div className="server-track" style={{ transform: `translateX(-${index * 100}%)` }}>
            {SERVERS.map((server, i) => (
              <article
                key={server.id}
                id={`server-slide-${server.id}`}
                role="tabpanel"
                aria-hidden={i !== index}
                className={`server-slide ${i === index ? 'active' : ''}`}
              >
                <div className="server-visual">{server.visual}</div>
                <div className="server-info">
                  <div className="server-badge">{server.badge}</div>
                  <h2>{server.name}</h2>
                  <p className="server-tagline">{server.tagline}</p>
                  <p>{server.blurb}</p>
                  <div className="server-features">
                    {server.features.map((f) => <span key={f}>{f}</span>)}
                  </div>
                  <div className="server-actions">
                    <a href={server.primary.href} className="btn btn-server" target="_blank" rel="noopener noreferrer"
                       tabIndex={i === index ? 0 : -1}>
                      {server.primary.label}
                    </a>
                    <a href={server.secondary.href} className="btn btn-ghost btn-sm" target="_blank" rel="noopener noreferrer"
                       tabIndex={i === index ? 0 : -1}>
                      {server.secondary.label}
                    </a>
                    {server.address && (
                      <button type="button" className="server-address" onClick={() => copy(server.address)}
                              tabIndex={i === index ? 0 : -1} title="Copy the server address">
                        {copied === server.address ? 'Copied!' : server.address}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="server-dots" aria-hidden="true">
          {SERVERS.map((server, i) => (
            <span key={server.id} className={`server-dot ${i === index ? 'active' : ''}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
